import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import { ArrowBack, Edit, PlaylistAddCheck } from "@mui/icons-material";
import { useCampaign, useCampaignStats } from "@/hooks/useCampaigns";
import { CAMPAIGN_STATUS_COLORS, CAMPAIGN_STATUS_LABELS } from "@/types";
import GenerateFollowupSequenceDialog from "@/components/GenerateFollowupSequenceDialog";

export default function CampaignDetail() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();

  const { data: campaign, isLoading, error } = useCampaign(campaignId);
  const { data: stats } = useCampaignStats(campaignId);
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !campaign) {
    return <Alert severity="error">Campaign not found.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/campaigns")}>
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          {campaign.name}
        </Typography>
        <Chip
          label={CAMPAIGN_STATUS_LABELS[campaign.status]}
          color={CAMPAIGN_STATUS_COLORS[campaign.status]}
        />
        <Button
          variant="outlined"
          startIcon={<PlaylistAddCheck />}
          onClick={() => setSequenceDialogOpen(true)}
          title="Generate a multi-touch follow-up sequence for selected contacts"
        >
          Generate sequence
        </Button>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
        >
          Edit
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {campaign.description && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Description
                </Typography>
                <Typography>{campaign.description}</Typography>
              </CardContent>
            </Card>
          )}

          {campaign.goal && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Goal
                </Typography>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>{campaign.goal}</Typography>
              </CardContent>
            </Card>
          )}

          {campaign.notes && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Notes
                </Typography>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>{campaign.notes}</Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Activity
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography>
                  <strong>{stats?.communication_count ?? 0}</strong> communications logged
                </Typography>
                <Typography>
                  <strong>{stats?.followup_count ?? 0}</strong> follow-ups total
                  {stats?.pending_followup_count ? (
                    <span style={{ color: "#E65100" }}> ({stats.pending_followup_count} open)</span>
                  ) : null}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {(campaign.target_segment ||
            campaign.owner_agent ||
            campaign.start_date ||
            campaign.end_date) && (
            <Card>
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Setup
                </Typography>
                {campaign.target_segment && (
                  <Typography variant="body2">
                    <strong>Target:</strong> {campaign.target_segment}
                  </Typography>
                )}
                {campaign.owner_agent && (
                  <Typography variant="body2">
                    <strong>Owner:</strong> {campaign.owner_agent}
                  </Typography>
                )}
                {campaign.start_date && (
                  <Typography variant="body2">
                    <strong>Starts:</strong> {campaign.start_date}
                  </Typography>
                )}
                {campaign.end_date && (
                  <Typography variant="body2">
                    <strong>Ends:</strong> {campaign.end_date}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {campaign.tags && campaign.tags.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {campaign.tags.map((t) => (
                    <Chip key={t} label={t} size="small" variant="outlined" />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {campaignId && (
        <GenerateFollowupSequenceDialog
          open={sequenceDialogOpen}
          onClose={() => setSequenceDialogOpen(false)}
          campaignId={campaignId}
          onGenerated={(result) => {
            setSnackbar({
              open: true,
              message:
                result.created_followup_count > 0
                  ? `Generated ${result.created_followup_count} tasks across ${result.individuals_processed} contact(s)` +
                    (result.skipped_existing_count > 0
                      ? ` (${result.skipped_existing_count} already existed)`
                      : "")
                  : `All ${result.skipped_existing_count} touch(es) already existed for these contacts — nothing to create`,
              severity: "success",
            });
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
